import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Instagram, Facebook, Sparkles } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [promo, setPromo] = useState(null);
  const [promoDismissed, setPromoDismissed] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const fetchPromo = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/promo`);
        if (response.data.active && response.data.message) {
          setPromo(response.data.message);
        } else {
          setPromo(null);
        }
      } catch (error) {
        setPromo(null);
      }
    };
    fetchPromo();
  }, [location.pathname]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
      {promo && !promoDismissed && (
        <div
          className="relative text-center py-2 px-10 text-white text-sm font-bold"
          style={{ background: 'linear-gradient(90deg, #d4a017, #ec4899, #9333ea)' }}
          data-testid="promo-banner"
        >
          <Sparkles className="w-4 h-4 inline-block mr-2 -mt-0.5" />
          {promo}
          <button
            onClick={() => setPromoDismissed(true)}
            aria-label="Dismiss promo"
            data-testid="promo-dismiss-button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <nav className="container mx-auto px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Desktop Navigation - Now on Left */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/" 
              className={`text-base font-medium transition-colors duration-200 ${
                isActive('/') ? 'text-pink-600' : 'text-gray-700 hover:text-pink-600'
              }`}
            >
              Home
            </Link>
            <Link 
              to="/portfolio" 
              className={`text-base font-medium transition-colors duration-200 ${
                isActive('/portfolio') ? 'text-amber-600' : 'text-gray-700 hover:text-amber-600'
              }`}
            >
              Portfolio
            </Link>
            <Link 
              to="/events" 
              className={`text-base font-medium transition-colors duration-200 ${
                isActive('/events') ? 'text-purple-600' : 'text-gray-700 hover:text-purple-600'
              }`}
            >
              Book Now
            </Link>
            <Link 
              to="/testimonials" 
              className={`text-base font-medium transition-colors duration-200 ${
                isActive('/testimonials') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Testimonials
            </Link>
            <Link 
              to="/contact" 
              className={`text-base font-medium transition-colors duration-200 ${
                isActive('/contact') ? 'text-pink-600' : 'text-gray-700 hover:text-pink-600'
              }`}
            >
              Contact
            </Link>
          </div>

          {/* Social Media Icons - Now on Right */}
          <div className="flex items-center gap-4">
            {/* Social Media Icons - Desktop */}
            <div className="hidden md:flex items-center gap-2">
              <a 
                href="https://www.instagram.com/creativemindsfargo/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gradient-to-br from-pink-300 to-yellow-300 rounded-lg flex items-center justify-center hover:scale-110 transition-transform duration-200"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4 text-gray-900" />
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=61583622694458"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gradient-to-br from-blue-300 to-pink-300 rounded-lg flex items-center justify-center hover:scale-110 transition-transform duration-200"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4 text-gray-900" />
              </a>
              <a 
                href="https://www.tiktok.com/@creativemindsfargo"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gradient-to-br from-pink-400 to-blue-400 rounded-lg flex items-center justify-center hover:scale-110 transition-transform duration-200"
                aria-label="TikTok"
              >
                <svg className="w-4 h-4 text-gray-900" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6 text-gray-700" /> : <Menu className="w-6 h-6 text-gray-700" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
            <div className="flex flex-col gap-4">
              <Link 
                to="/" 
                onClick={() => setIsOpen(false)}
                className={`text-base font-medium ${isActive('/') ? 'text-pink-600' : 'text-gray-700'}`}
              >
                Home
              </Link>
              <Link 
                to="/portfolio" 
                onClick={() => setIsOpen(false)}
                className={`text-base font-medium ${isActive('/portfolio') ? 'text-amber-600' : 'text-gray-700'}`}
              >
                Portfolio
              </Link>
              <Link 
                to="/events" 
                onClick={() => setIsOpen(false)}
                className={`text-base font-medium ${isActive('/events') ? 'text-purple-600' : 'text-gray-700'}`}
              >
                Book Now
              </Link>
              <Link 
                to="/testimonials" 
                onClick={() => setIsOpen(false)}
                className={`text-base font-medium ${isActive('/testimonials') ? 'text-blue-600' : 'text-gray-700'}`}
              >
                Testimonials
              </Link>
              <Link 
                to="/contact" 
                onClick={() => setIsOpen(false)}
                className={`text-base font-medium ${isActive('/contact') ? 'text-pink-600' : 'text-gray-700'}`}
              >
                Contact
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};
